'use client'

import { SizeVariants } from '@/shared/forms/components/constants'
import React, { HTMLInputTypeAttribute, ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'
import clsx from 'clsx'
import { SimpleTooltip } from '@/shared/components/common/Tooltip'
import OptionalLabel from '@/shared/forms/components/OptionalLabel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons'

type TextAreaProps = {
  variant: 'multiline'
  readonly?: boolean
  rows?: number
  resizable?: boolean
}

type TextInputProps = {
  variant?: 'text'
  verified?: boolean
}

export type RHFTextInputProps = {
  name: string
  label: string
  size?: keyof typeof SizeVariants
  hideLabel?: boolean
  tooltip?: ReactNode
  optional?: boolean
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search'
  inputClassname?: string
  autoComplete?: string
  disabled?: boolean
  placeholder?: string
  type?: HTMLInputTypeAttribute
  hasError?: boolean
  contentAfterLabel?: ReactNode
} & (TextAreaProps | TextInputProps)

export default function RHFTextInput(props: RHFTextInputProps) {
  const {
    // common props
    name,
    label,
    size = 'full',
    hideLabel,
    tooltip,
    optional,

    // input specific props
    inputMode,
    inputClassname,
    autoComplete,
    disabled,
    placeholder,
    type,
    hasError,

    // other optional props
    contentAfterLabel,
  } = props

  const { register, watch, formState } = useFormContext()
  const { isSubmitting, errors, touchedFields } = formState
  const isDisabled = disabled || isSubmitting
  const value = watch(name)
  const field = register(name)
  const touched = touchedFields?.[name]
  const error = errors[name]

  const children = (() => {
    switch (props.variant) {
      case 'text':
      case undefined:
        const { verified = false } = props

        return (
          <>
            <input
              {...field}
              type={type}
              className={clsx(
                'block h-10 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6',
                (touched && error) || hasError
                  ? 'ring-red-600'
                  : 'ring-gray-300',
                isDisabled ? 'bg-slate-300' : '',
                inputClassname,
              )}
              disabled={isDisabled}
              id={name}
              placeholder={placeholder}
              inputMode={inputMode}
              autoComplete={autoComplete}
            />
            {verified && (
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <FontAwesomeIcon
                  icon={faBadgeCheck}
                  className="h-5 w-5 text-green-500"
                  aria-hidden="true"
                />
              </div>
            )}
          </>
        )
      case 'multiline':
        const { resizable = true, readonly, rows = 5 } = props
        return (
          <textarea
            {...field}
            className={clsx(
              'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-inset placeholder:text-gray-400 sm:text-sm sm:leading-6',
              (touched && error) || hasError ? 'ring-red-600' : 'ring-gray-300',
              isDisabled ? 'bg-slate-300' : '',
              !readonly
                ? 'ring-1 focus:ring-2 focus:ring-inset focus:ring-blue-600'
                : 'bg-slate-100 ring-0 focus:ring-0',
              resizable ? '' : 'resize-none',
              inputClassname,
            )}
            rows={rows}
            disabled={isDisabled}
            value={value}
            placeholder={placeholder}
            readOnly={readonly}
            inputMode={inputMode}
            autoComplete={autoComplete}
          />
        )
    }
  })()

  return (
    <div className={SizeVariants[size as keyof typeof SizeVariants]}>
      <div className="flex items-end justify-between">
        <div className="flex items-center gap-x-2">
          <label
            htmlFor={name}
            className={clsx(
              'block text-sm font-medium leading-6',
              disabled ? 'text-gray-500' : 'text-gray-900',
              hideLabel && 'sr-only',
            )}
          >
            {label}
            {contentAfterLabel}
          </label>
          {tooltip && <SimpleTooltip content={tooltip} />}
        </div>
        <OptionalLabel optional={optional === true} />
      </div>
      <div className="relative mt-2">
        {children}
        {touched && error && (
          <div className="mt-2 text-sm font-semibold text-red-600">{`${error.message}`}</div>
        )}
      </div>
    </div>
  )
}
